import React, { useEffect, useState } from 'react';
import { getProductTypes, updateProductType, addInventoryItem, getInventoryItems } from '../../api/inventory';
import { useNavigate } from 'react-router-dom';
import TransactionAmount, { ProductItem } from 'api/inventory-types';

const ProductList = () => {
    const [products, setProducts] = useState<ProductItem[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editPrice, setEditPrice] = useState<string>('');
    const navigate = useNavigate();

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        const items = await getProductTypes();
        setProducts(items);
    };

    const handleUpdatePrice = async (productId: string) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;

        const updatedProduct = new ProductItem(
            product.id, 
            product.name, 
           new TransactionAmount(new Map([['points', parseFloat(editPrice)]])),
            product.type, 
            product.deliveryMethod,
            product.numberInStock,
            product.isAmbassador,
            product.isPremium);

        const response = await updateProductType(updatedProduct);
        if (response.status === 200) {
            setEditingId(null);
            loadProducts();
        }
    };

    const handleAddStock = async (productId: string) => {
        navigate(`/admin/products/${productId}/add-stock`);
    };

    const [inventory, setInventory] = useState<Record<string, number>>({});

    useEffect(() => {
        loadInventoryForProducts();
    }, [products]);

    const loadInventoryForProducts = async () => {
        const inventoryData: Record<string, number> = {};
        for (const product of products) {
            const items = await getInventoryItems(product.id);
            inventoryData[product.id] = items.reduce((sum: number, item: any) => sum + item.amount, 0);
        }
        setInventory(inventoryData);
    };

    const handleAddProductType = () => {
        navigate('/admin/products/create');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold mb-4">Product Types</h2>
            <div className="bg-white shadow-md rounded-lg">
                <table className="min-w-full">
                    <thead>
                        <tr>
                            <th className="px-6 py-3 border-b">Name</th>
                            <th className="px-6 py-3 border-b">Price</th>
                            <th className="px-6 py-3 border-b">Inventory</th>
                            <th className="px-6 py-3 border-b">Type</th>
                            <th className="px-6 py-3 border-b">Status</th>
                            <th className="px-6 py-3 border-b">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(product => (
                            <tr key={product.id}>
                                <td className="px-6 py-4 border-b">{product.id}</td>
                                <td className="px-6 py-4 border-b">
                                    {editingId === product.id ? (
                                        <input
                                            type="number"
                                            value={editPrice}
                                            onChange={(e) => setEditPrice(e.target.value)}
                                            className="border rounded px-2 py-1"
                                        />
                                    ) : (
                                        `${product.price.values.get('points')}`
                                    )}
                                </td>
                                <td className="px-6 py-4 border-b">
                                    {product.deliveryMethod === 'virtual_product' ? 'N/A' : (
                                        inventory[product.id] || 0
                                    )}
                                </td>
                                <td className="px-6 py-4 border-b">
                                    {`${product.deliveryMethod === 'virtual_product' ? 'Virtual' : 
                                       product.deliveryMethod === 'digital_product' ? 'Digital' : 'Physical'} / 
                                     ${product.type === 'permanent' ? 'Permanent' : 'Consumable'}`}
                                </td>
                                <td className="px-6 py-4 border-b">
                                    <div className="flex flex-wrap gap-2">
                                        {product.isAmbassador && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                Ambassador
                                            </span>
                                        )}
                                        {product.isPremium && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Premium
                                            </span>
                                        )}
                                        {!product.isAmbassador && !product.isPremium && (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 border-b">
                                    <div className="flex space-x-2">
                                        {editingId === product.id ? (
                                            <button
                                                onClick={() => handleUpdatePrice(product.id)}
                                                className="bg-green-500 text-white px-4 py-2 rounded"
                                            >
                                                Save
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setEditingId(product.id);
                                                    setEditPrice(product.price.values.get('points')?.toString() || '0');
                                                }}
                                                className="bg-blue-500 text-white px-4 py-2 rounded"
                                            >
                                                Edit Price
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleAddStock(product.id)}
                                            className="bg-green-500 text-white px-4 py-2 rounded"
                                        >
                                            Add Stock
                                        </button>
                                        <button
                                            onClick={() => navigate(`/admin/products/${product.id}`)}
                                            className="bg-gray-500 text-white px-4 py-2 rounded"
                                        >
                                            Details
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-4">
                <button
                    onClick={() => handleAddProductType()}
                    className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
                >
                    Add New Product Type
                </button>
            </div>
        </div>
    );
};

export default ProductList; 